import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

/**
 * iOS-style edge-swipe-to-go-back gesture.
 *
 * Wrap any page with this component to enable a "swipe from the left edge
 * to the right" gesture that navigates back — the same interaction Safari,
 * Uber, Instagram, etc. use. Capacitor WKWebView doesn't emit the native
 * edge-swipe gesture, so we reimplement it via touch events.
 *
 * Behaviour:
 *   • Touch must start within the first `EDGE_ZONE` px from the left edge.
 *   • Vertical drift must stay below `MAX_VERTICAL` px (otherwise it's a
 *     regular scroll, not a swipe).
 *   • Release past `THRESHOLD` px → navigate(-1) with a smooth fade+slide.
 *   • Below threshold → snap back to origin.
 *
 * Props:
 *   - fallback : optional path to go to when there is no history entry to
 *                pop (react-router `navigate(-1)` no-ops in that case).
 *                Defaults to the driver dashboard for driver pages, etc.
 */
export default function SwipeBack({ children, fallback = "/" }) {
  const navigate = useNavigate();
  const startXRef = useRef(null);
  const startYRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const EDGE_ZONE = 32;   // px from the left edge that counts as an "edge grab"
  const THRESHOLD = 90;   // px to travel to trigger the back nav
  const MAX_VERTICAL = 40;

  const goBack = () => {
    // If the browser stack has entries, pop one. Otherwise fall back to a
    // sensible landing page so the user isn't stuck on a blank screen.
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallback, { replace: true });
    }
  };

  useEffect(() => {
    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      // Only start the gesture if the finger lands near the left edge.
      if (t.clientX > EDGE_ZONE) return;
      startXRef.current = t.clientX;
      startYRef.current = t.clientY;
      setDragging(true);
      setDragX(0);
    };
    const onTouchMove = (e) => {
      if (startXRef.current == null || !e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - startXRef.current;
      const dy = Math.abs(t.clientY - (startYRef.current ?? 0));
      // Abort if the user is clearly scrolling vertically.
      if (dy > MAX_VERTICAL && dx < 8) {
        startXRef.current = null;
        setDragging(false);
        setDragX(0);
        return;
      }
      if (dx < 0) return;
      setDragX(dx);
    };
    const onTouchEnd = () => {
      if (startXRef.current == null) return;
      const dx = dragX;
      startXRef.current = null;
      setDragging(false);
      if (dx >= THRESHOLD) {
        // Trigger back nav with a short slide-out animation.
        setDragX(window.innerWidth);
        setTimeout(() => {
          setDragX(0);
          goBack();
        }, 180);
      } else {
        // Snap back.
        setDragX(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragX]);

  const hintOpacity = Math.min(1, dragX / THRESHOLD);
  const shift = Math.min(dragX, 220);

  return (
    <div
      className="relative"
      style={{
        transform: shift > 0 ? `translateX(${shift}px)` : undefined,
        transition: dragging ? "none" : "transform 200ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      {/* Visual hint that follows the finger from the left edge. */}
      {dragX > 6 && (
        <div
          className="pointer-events-none fixed left-2 top-1/2 -translate-y-1/2 z-[60]"
          style={{
            opacity: hintOpacity,
            transform: `translate(-50%, -50%) scale(${0.7 + hintOpacity * 0.3})`,
            left: Math.min(dragX / 2, 40),
          }}
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
            <ChevronLeft className="h-5 w-5" />
          </div>
        </div>
      )}
      {children}
    </div>
  );
}
